import { useState, useEffect } from 'react';
import { ProfileCard, ChangePasswordCard } from '@podus/ui';
import { api } from '@/lib/api';
import type { StaffMember, FeeConfig } from '@/types';
import UploadLogoCard from './components/UploadLogoCard';
import PaymentDetailsCard from './components/PaymentDetailsCard';

export default function Settings() {
    const [profile, setProfile] = useState<StaffMember | null>(null);
    const [logoPath, setLogoPath] = useState<string | null>(null);
    const [feeConfigs, setFeeConfigs] = useState<FeeConfig[]>([]);

    const fetchData = async () => {
        try {
            const [profileRes, logoRes, feesRes] = await Promise.all([
                api.get<StaffMember>('/api/settings/profile'),
                api.get<{ logoPath: string | null }>('/api/settings/logo'),
                api.get<FeeConfig[]>('/api/settings/fees'),
            ]);

            if (profileRes.response.data) setProfile(profileRes.response.data);
            if (logoRes.response.data) setLogoPath(logoRes.response.data.logoPath);
            if (feesRes.response.data) setFeeConfigs(feesRes.response.data);
        } catch (err) {
            console.error('Failed to fetch settings data:', err);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    const defaultPaymentCards = [
        { caseCategory: 'Walk-in', items: [], totalFee: 0, id: 0 },
        { caseCategory: 'Court Referral', items: [], totalFee: 0, id: 0 },
        { caseCategory: 'Online', items: [], totalFee: 0, id: 0 },
    ] as FeeConfig[];

    const displayFeeConfigs = defaultPaymentCards.map((def) => {
        const found = feeConfigs.find((c) => c.caseCategory === def.caseCategory);
        return found || def;
    });

    return (
        <div className="px-4 py-6 lg:px-8">
            <h1 className="mb-6 text-2xl font-bold text-secondary sm:text-3xl">Settings</h1>

            <div className="grid gap-5 lg:grid-cols-3">
                <ProfileCard
                    profile={profile}
                    onSave={async (updates) => {
                        await api.patch('/api/settings/profile', updates);
                        await fetchData();
                    }}
                />
                <ChangePasswordCard
                    onSubmit={async (data) => {
                        await api.post('/api/settings/change-password', data);
                    }}
                />
                <UploadLogoCard initialLogo={logoPath} />
            </div>

            <div className="mt-6 grid gap-5 lg:grid-cols-3">
                {displayFeeConfigs.map((config) => (
                    <PaymentDetailsCard key={config.caseCategory} config={config} onUpdate={fetchData} />
                ))}
            </div>
        </div>
    );
}
